import { useUser, useClerk } from '@clerk/clerk-react';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { FileSliders, LogOut, Flag, Scale, Settings, Gift, ChevronRight } from 'lucide-react';
import Preferences from '../../src/auth/preferences';

const AccountsMenu = ({ isMorePage = false, placement = 'default', collapsed = false }) => {
    const { user, isLoaded: isUserLoaded, isSignedIn } = useUser();
    const { signOut, openUserProfile } = useClerk();
    const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const menuRef = useRef(null);

    const roles = useSelector(s => s.auth.roles) || [];
    const rolesStatus = useSelector(s => s.auth.rolesStatus);

    const userRole = useMemo(() => {
        if (rolesStatus === 'idle' || rolesStatus === 'loading') return 'Loading...';
        if (Array.isArray(roles) && roles.length > 0) return roles.join(', ');
        return 'Student';
    }, [roles, rolesStatus]);

    const displayUsername = useMemo(() => {
        if (!isUserLoaded) return 'Loading...';
        if (!isSignedIn) return 'Sign in';
        const username = user?.username;
        if (username) return username;
        const email = user?.primaryEmailAddress?.emailAddress;
        if (email) return email.split('@')[0];
        return 'User';
    }, [isUserLoaded, isSignedIn, user?.username, user?.primaryEmailAddress?.emailAddress]);

    useEffect(() => {
        const handleClickOutside = event => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsMenuOpen(false);
            }
        };

        if (isMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isMenuOpen]);

    const menuItems = [
        {
            label: 'Manage Account',
            icon: Settings,
            onClick: () => {
                setIsMenuOpen(false);
                openUserProfile();
            },
        },
        {
            label: 'My Preferences',
            icon: FileSliders,
            onClick: () => {
                setIsMenuOpen(false);
                setIsPreferencesOpen(true);
            },
        },
        {
            label: 'Request Feature or Report Bug',
            icon: Flag,
            onClick: () => {
                setIsMenuOpen(false);
                window.open(
                    'https://teable.zinduaschool.com/share/shr02vkM9ypwxebB85H/view',
                    '_blank'
                );
            },
        },
        {
            label: 'Refer & Earn (Affiliate Program)',
            icon: Gift,
            onClick: () => {
                setIsMenuOpen(false);
                window.open('https://zinduaschool.com/refer/', '_blank');
            },
        },
        {
            label: 'Legal Policies (Terms/Privacy)',
            icon: Scale,
            onClick: () => {
                setIsMenuOpen(false);
                window.open(
                    'https://zinduaschool.com/legal/?srsltid=AfmBOoqOlEpreLAWY_4aXikZqmMIQ-xz0oJ1N_XTy_ZHRleJJmVWyqAf#terms',
                    '_blank'
                );
            },
        },
        {
            label: 'Sign Out',
            icon: LogOut,
            onClick: async () => {
                setIsMenuOpen(false);
                await signOut();
                window.location.href = '/';
            },
        },
    ];

    const toggleMenu = () => setIsMenuOpen(prev => !prev);

    const isSidebarPlacement = placement === 'sidebar';

    return (
        <>
            <div
                ref={menuRef}
                className={`relative flex items-center cursor-pointer gap-2 min-w-0 ${
                    isSidebarPlacement ? 'w-full' : ''
                }`}
            >
                {isSidebarPlacement ? (
                    <button
                        type="button"
                        onClick={toggleMenu}
                        className={collapsed ? 'shrink-0' : 'hidden md:flex w-full items-center min-w-0'}
                        aria-label="Open account menu"
                    >
                        <span className={collapsed ? '' : 'flex items-center gap-2 min-w-0 flex-1'}>
                            <img
                                src={user?.imageUrl}
                                alt={user?.username}
                                className="w-9 h-9 rounded-full object-cover border border-(--neutral-300)"
                            />
                            {!collapsed && (
                                <h5 className="font-heading truncate min-w-0 text-(--neutral-900)">
                                    {displayUsername}
                                </h5>
                            )}
                        </span>
                        {!collapsed && (
                            <ChevronRight
                                className="w-5 h-5 text-(--neutral-800) shrink-0 ml-auto"
                                strokeWidth={2.5}
                            />
                        )}
                    </button>
                ) : (
                    <>
                        <button
                            type="button"
                            onClick={toggleMenu}
                            className="shrink-0"
                            aria-label="Open account menu"
                        >
                            <img
                                src={user?.imageUrl}
                                alt={user?.username}
                                className="w-10 h-10 rounded-full object-cover border border-(--neutral-300)"
                            />
                        </button>

                        {!collapsed && (
                            <button
                                type="button"
                                onClick={toggleMenu}
                                className="hidden md:flex items-center justify-between gap-2 min-w-0 md:max-w-none max-w-55 flex-1"
                                aria-label="Open account menu"
                            >
                                <h5 className="font-heading truncate flex-1 min-w-0 text-(--neutral-900)">
                                    {displayUsername}
                                </h5>
                                <ChevronRight
                                    className="w-5 h-5 text-(--neutral-800) shrink-0"
                                    strokeWidth={2.5}
                                />
                            </button>
                        )}
                    </>
                )}

                {isMenuOpen && (
                    <div
                        className={
                            placement === 'sidebar'
                                ? 'absolute -top-100 left-0 w-[320px] max-w-[calc(100vw-16px)] bg-(--neutral-50) rounded-lg shadow-lg border border-(--neutral-300) py-2 z-9999'
                                : 'absolute right-0 mt-2 w-[320px] max-w-[calc(100vw-16px)] bg-(--neutral-50) rounded-lg shadow-lg border border-(--neutral-300) py-2 z-9999'
                        }
                    >
                        {/* User Profile Section */}
                        <div className="flex items-center gap-3 px-4 py-3">
                            <img
                                src={user?.imageUrl}
                                alt={user?.username}
                                className={`w-12 h-12 rounded-full object-cover border border-(--neutral-300)`}
                            />
                            <div className="flex flex-col min-w-0">
                                <span className="font-medium text-(--neutral-900) truncate capitalize">
                                    {user?.fullName || user?.firstName || 'User'}
                                </span>
                                <span className="text-sm text-(--neutral-600) truncate">
                                    @{displayUsername}
                                </span>
                                <span className="text-xs text-primary font-medium">{userRole}</span>
                            </div>
                        </div>
                        <div className="border-t border-(--neutral-300) my-1" />
                        {menuItems.map((item, index) => {
                            const Icon = item.icon;
                            return (
                                <div key={index}>
                                    <button
                                        onClick={item.onClick}
                                        className="w-full flex items-center gap-3 px-4 py-3 text-left text-(--neutral-700) hover:bg-(--neutral-100) transition-colors text-sm"
                                    >
                                        <Icon className="w-4 h-4 shrink-0" />
                                        <span>{item.label}</span>
                                    </button>
                                    {(index + 1) % 2 === 0 && index !== menuItems.length - 1 && (
                                        <div className="border-t border-(--neutral-300) my-1" />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <Preferences isOpen={isPreferencesOpen} onClose={() => setIsPreferencesOpen(false)} />
        </>
    );
};

export default AccountsMenu;
